// Kiti Wishlist — custom Growave wishlist header trigger + live count
//
// Opens the Growave wishlist drawer from any element carrying
// data-kiti-wishlist-trigger (or the .js-kiti-wishlist class), and keeps
// every .kiti-wishlist-count element in the DOM in sync with the current
// total number of wishlist items across all wishlists.
//
// Uses Growave's documented SDK shape:
//   - window.gw.sdk                                           (SDK namespace)
//   - "gwSdkReady" event on document                          (SDK available)
//   - window.gw.sdk.common.constants.modules.WISHLISTS        (module name)
//   - window.gw.sdk.runIfModuleReady(module, callback)        (per-module gate)
//   - window.gw.sdk.wishlists.methods.openWishlistsDrawer()   (opens drawer)
//   - window.gw.sdk.wishlists.methods.getWishlistsSharedStateKit()
//       -> stateKit.query.selectAll().subscribe(wishlists => ...)
// See docs.growave.io/docs/Sdk/reference/wishlists

(function () {
  var pending = [];
  var wishlistsReady = false;
  var currentCount = 0;

  // ---------------------------------------------------------------------
  // Open the drawer
  // ---------------------------------------------------------------------

  function flush() {
    while (pending.length) {
      var fn = pending.shift();
      try { fn(); } catch (err) { console.warn('[KitiWishlist] handler failed', err); }
    }
  }

  function callOpenDrawer() {
    try {
      var result = window.gw.sdk.wishlists.methods.openWishlistsDrawer();
      if (result && typeof result.catch === 'function') {
        result.catch(function (err) { console.warn('[KitiWishlist] openWishlistsDrawer failed', err); });
      }
    } catch (err) {
      console.warn('[KitiWishlist] openWishlistsDrawer threw', err);
    }
  }

  function openWishlist() {
    if (wishlistsReady) {
      callOpenDrawer();
      return;
    }
    pending.push(callOpenDrawer);
  }

  // ---------------------------------------------------------------------
  // Count badge — updates every .kiti-wishlist-count element in the DOM
  // ---------------------------------------------------------------------

  function renderCount(count) {
    currentCount = count;
    var nodes = document.querySelectorAll('.kiti-wishlist-count');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (count > 0) {
        node.textContent = count > 99 ? '99+' : String(count);
        node.removeAttribute('hidden');
      } else {
        node.textContent = '';
        node.setAttribute('hidden', '');
      }
    }
  }

  function countItemsInWishlists(wishlists) {
    if (!wishlists || !wishlists.length) return 0;
    var total = 0;
    for (var i = 0; i < wishlists.length; i++) {
      var w = wishlists[i];
      if (w && w.items && typeof w.items.length === 'number') {
        total += w.items.length;
      }
    }
    return total;
  }

  function subscribeToWishlistState() {
    try {
      window.gw.sdk.wishlists.methods.getWishlistsSharedStateKit()
        .then(function (response) {
          if (!response || !response.result) {
            console.warn('[KitiWishlist] state kit unavailable', response);
            return;
          }
          var stateKit = response.result;
          stateKit.query.selectAll().subscribe(function (wishlists) {
            renderCount(countItemsInWishlists(wishlists));
          });
          // Prime the state kit on page load. Without this, the subscription
          // sits at its initial empty emission and only fires again when the
          // user changes the wishlist — so a fresh page load with items
          // already in the wishlist shows no badge until the next change.
          primeInitialCount();
        })
        .catch(function (err) {
          console.warn('[KitiWishlist] getWishlistsSharedStateKit failed', err);
        });
    } catch (err) {
      console.warn('[KitiWishlist] state kit subscription threw', err);
    }
  }

  // Fetch current wishlists directly on page load. Growave's shared state
  // kit does not auto-populate on init — the subscription only fires once
  // the underlying data actually changes. Calling getWishlists() here
  // populates the kit (which triggers the subscription with real data)
  // and also gives us a belt-and-braces direct render in case the state
  // kit pipeline fails for any reason.
  function primeInitialCount() {
    try {
      var result = window.gw.sdk.wishlists.methods.getWishlists();
      if (!result || typeof result.then !== 'function') return;
      result.then(function (response) {
        if (!response) return;
        // getWishlists() may return either { result: [...] } or the array directly
        var wishlists = response.result || response;
        if (Array.isArray(wishlists)) {
          renderCount(countItemsInWishlists(wishlists));
        }
      }).catch(function (err) {
        console.warn('[KitiWishlist] primeInitialCount getWishlists failed', err);
      });
    } catch (err) {
      console.warn('[KitiWishlist] primeInitialCount threw', err);
    }
  }

  // ---------------------------------------------------------------------
  // Hide Growave's default wishlist header icon
  //
  // Growave injects its own heart icon inside the `gw-wl-header-icon-placeholder`
  // element whenever it exists. Our custom header markup replaces that
  // placeholder entirely, but as belt-and-braces we also hide any
  // wishlist header icon Growave may still mount (e.g. if something else
  // in the theme re-inserts the placeholder). The rewards launcher taught
  // us that Growave uses shadow roots, so we inject a scoped <style> tag
  // inside each shadow root we can find — the same pattern used by
  // kiti-rewards.js.
  // ---------------------------------------------------------------------

  var HIDE_CSS =
    '.gw-wl-header-icon-placeholder,' +
    '[class*="gw-wl-header-icon"],' +
    '[class*="gw-wishlist-header"],' +
    '[class*="wl-header-icon"]' +
    '{display:none !important;visibility:hidden !important;}';

  function injectHideStyleInto(shadowRoot) {
    if (!shadowRoot) return false;
    if (shadowRoot.getElementById && shadowRoot.getElementById('kiti-wishlist-hide-style')) {
      return true;
    }
    try {
      var style = document.createElement('style');
      style.id = 'kiti-wishlist-hide-style';
      style.textContent = HIDE_CSS;
      shadowRoot.appendChild(style);
      return true;
    } catch (err) {
      console.warn('[KitiWishlist] shadow-root style injection failed', err);
      return false;
    }
  }

  function sweepShadowRoots() {
    var hosts = document.querySelectorAll(
      '.gw-wl-header-icon-placeholder, ' +
      '[class*="gw-widget-placeholder"], ' +
      '[class*="gw-wl-"], ' +
      '[class^="gw-"]'
    );
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].shadowRoot) {
        injectHideStyleInto(hosts[i].shadowRoot);
      }
    }
    // Also hide any placeholder directly in the light DOM.
    var placeholders = document.querySelectorAll('.gw-wl-header-icon-placeholder');
    for (var j = 0; j < placeholders.length; j++) {
      placeholders[j].style.display = 'none';
    }
  }

  function startShadowObserver() {
    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(function () { sweepShadowRoots(); });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { observer.disconnect(); }, 30000);
  }

  // ---------------------------------------------------------------------
  // SDK wiring
  // ---------------------------------------------------------------------

  function onWishlistsModuleReady() {
    wishlistsReady = true;
    flush();
    subscribeToWishlistState();
  }

  function onSdkReady() {
    var WISHLISTS = window.gw.sdk.common.constants.modules.WISHLISTS;
    // runIfModuleReady fires its callback as soon as the module is ready
    // (or immediately if it already is), so we don't need to wait on a
    // WIDGET_INJECTED event for wishlists specifically.
    window.gw.sdk.runIfModuleReady(WISHLISTS, onWishlistsModuleReady);
  }

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      sweepShadowRoots();
      startShadowObserver();
    });
  } else {
    sweepShadowRoots();
    startShadowObserver();
  }

  if (window.gw && window.gw.sdk) {
    onSdkReady();
  } else {
    document.addEventListener('gwSdkReady', onSdkReady);
  }

  // Expose globals for anything else in the theme.
  window.openKitiWishlist = openWishlist;
  window.getKitiWishlistCount = function () { return currentCount; };

  // Delegated click handler — any element in the DOM with the data attr,
  // class, or an <a href="#kiti-wishlist"> triggers the drawer.
  document.addEventListener('click', function (event) {
    var target = event.target.closest(
      '[data-kiti-wishlist-trigger], .js-kiti-wishlist, a[href="#kiti-wishlist"], a[href$="/#kiti-wishlist"]'
    );
    if (!target) return;
    event.preventDefault();
    openWishlist();
  });
})();
